/**
 * Verbeterregister — /verbeterregister (gap-analyse 26 sep, #5).
 *
 * Functioneel gelijk aan de Airtable-interface: een bord met een kolom per status, filters,
 * slepen = status wijzigen (met ongedaan maken), nieuw verbeterpunt en detail bewerken.
 * Airtable blijft de bron; de engine leest uit de spiegel en schrijft naar Airtable.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { verbeterApi } from './verbeterApi';
import { LEEG_FILTER, eigenaar, filterPunten, herstelVanAntwoord, korteDatum, perStatus, platteTekst } from './verbeterLogica';
import { useUserRole } from '../../contexts/UserRoleContext';
import VerbeterpuntDialog from './VerbeterpuntDialog';

const tweeRegels = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

function Kaart({ p, onOpen, sleepbaar }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: p.id, disabled: !sleepbaar });
  const project = (p.projecten || []).map((x) => x.nummer || x.naam).filter(Boolean).join(', ');
  const meta = [p.nummer ? `#${p.nummer}` : null, project, p.fase].filter(Boolean).join(' · ');
  const labels = [p.impact && `Impact ${p.impact.toLowerCase()}`, p.urgentie, p.type].filter(Boolean).join(' · ');
  const wie = eigenaar(p);
  return (
    <Paper ref={setNodeRef} elevation={0} {...listeners} {...attributes} onClick={() => onOpen(p)}
      style={{ transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined, opacity: isDragging ? 0.4 : 1 }}
      sx={{ p: 1.5, mb: 1, borderRadius: 2, border: '1px solid #e4e7ec', cursor: sleepbaar ? 'grab' : 'pointer', bgcolor: '#fff',
        '&:hover': { borderColor: '#c3c9d4' } }}>
      <Typography sx={{ fontSize: 14, fontWeight: 650, lineHeight: 1.3, ...tweeRegels }}>{p.onderwerp || 'Zonder onderwerp'}</Typography>
      {p.verbeterpunten && (
        <Typography sx={{ fontSize: 12.5, color: '#64748b', mt: 0.5, lineHeight: 1.35, ...tweeRegels }}>{platteTekst(p.verbeterpunten)}</Typography>
      )}
      {meta && <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.75 }}>{meta}</Typography>}
      {labels && <Typography sx={{ fontSize: 11.5, color: p.impact === 'High' || p.urgentie === 'Nu' ? '#475569' : '#94a3b8', mt: 0.25 }}>{labels}</Typography>}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
        <Typography sx={{ fontSize: 11.5, color: '#475569', fontWeight: 600 }} noWrap>{wie || '—'}</Typography>
        <Typography sx={{ fontSize: 11.5, color: '#94a3b8', flexShrink: 0, ml: 1 }}>{korteDatum(p.aangemaaktOp)}</Typography>
      </Stack>
    </Paper>
  );
}

function Kolom({ status, punten, onOpen, sleepbaar, bezig }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <Box ref={setNodeRef} sx={{ flex: '1 1 0', minWidth: 250, bgcolor: isOver ? '#eef2ff' : '#f6f7f9', borderRadius: 2, p: 1, transition: 'background .12s' }}>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ px: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{status}</Typography>
        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{punten.length}</Typography>
      </Stack>
      {punten.map((p) => <Kaart key={p.id} p={p} onOpen={onOpen} sleepbaar={sleepbaar && !bezig?.has(p.id)} />)}
      {punten.length === 0 && <Typography sx={{ fontSize: 12, color: '#b0b8c4', px: 0.5, py: 1 }}>Leeg</Typography>}
    </Box>
  );
}

export default function Verbeterregister() {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [filter, setFilter] = useState(LEEG_FILTER);
  const [dialoog, setDialoog] = useState(null); // null | { punt: obj|null }
  const [params, setParams] = useSearchParams();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const laad = useCallback(async (vers = false) => {
    setLaden(true);
    try { setData(await verbeterApi.lijst(vers)); } catch (e) { toast.error(`Verbeterregister laden mislukt: ${e.message}`); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Deep link vanuit een overleg-issue: /verbeterregister?punt=rec...
  useEffect(() => {
    const id = params.get('punt');
    if (!id || !data) return;
    const p = data.punten.find((x) => x.id === id);
    if (p) setDialoog({ punt: p });
  }, [params, data]);

  const opties = useMemo(() => data?.opties || {}, [data]);
  const statussen = useMemo(() => opties.status || [], [opties]);
  const zichtbareStatussen = filter.statussen.length ? statussen.filter((s) => filter.statussen.includes(s)) : statussen;
  const gefilterd = useMemo(() => filterPunten(data?.punten || [], filter), [data, filter]);
  const kolommen = useMemo(() => perStatus(gefilterd, statussen), [gefilterd, statussen]);
  const { permissions } = useUserRole() || {};
  // Bewerken alleen met schrijfrecht op Airtable (niet op test) én edit-recht op 'overleg'.
  const schrijven = !!data?.schrijvenAan && !!permissions?.overleg?.edit;
  // Kaarten met een lopende wijziging: niet opnieuw sleepbaar tot de server antwoordt
  // (anders kan een oudere status winnen of een oude fout een nieuwere stand terugzetten).
  const [bezig, setBezig] = useState(() => new Set());
  const markeer = (id, aan) => setBezig((b) => { const n = new Set(b); if (aan) n.add(id); else n.delete(id); return n; });

  const vervang = (punt) => setData((d) => ({ ...d, punten: d.punten.some((x) => x.id === punt.id)
    ? d.punten.map((x) => (x.id === punt.id ? punt : x)) : [punt, ...d.punten] }));

  const onDragEnd = async ({ active, over }) => {
    if (!over || !schrijven || bezig.has(active.id)) return;
    const p = data.punten.find((x) => x.id === active.id);
    if (!p || p.status === over.id) return;
    markeer(p.id, true);
    vervang({ ...p, status: over.id }); // optimistisch
    let antwoord;
    try { antwoord = await verbeterApi.wijzig(p.id, { status: over.id }); }
    catch (e) { vervang(p); toast.error(e.message); return; }
    finally { markeer(p.id, false); }
    vervang(antwoord.punt);
    const herstel = herstelVanAntwoord(antwoord);
    if (!herstel) return;
    biedOngedaanAan(`"${p.onderwerp}" → ${over.id}`, async () => {
      markeer(p.id, true);
      try { vervang((await verbeterApi.wijzig(p.id, herstel)).punt); } finally { markeer(p.id, false); }
    });
  };

  const sluitDialoog = () => { setDialoog(null); if (params.get('punt')) { params.delete('punt'); setParams(params, { replace: true }); } };
  const zetF = (k) => (e) => setFilter((f) => ({ ...f, [k]: e.target.value }));
  const filterActief = Object.entries(filter).some(([, v]) => (Array.isArray(v) ? v.length : v));

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="trending_up" title="Verbeterregister"
        subtitle="Verbeterpunten uit projecten en overleggen, van bespreken tot geborgd"
        accent={{ bg: '#e2e8f0', color: '#334155' }}
        rightActions={schrijven && (
          <Button variant="contained" disableElevation size="small" onClick={() => setDialoog({ punt: null })} sx={{ textTransform: 'none', fontWeight: 700 }}>＋ Verbeterpunt</Button>
        )} />

      {data && !data.schrijvenAan && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>Schrijven naar Airtable staat uit op test — je kunt hier kijken, niet wijzigen.</Alert>
      )}
      {data?.melding && <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>{data.melding}</Alert>}

      <Paper variant="outlined" sx={{ p: 1.25, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField size="small" placeholder="Zoeken (onderwerp, project, persoon, #nr)" value={filter.zoek} onChange={zetF('zoek')} sx={{ minWidth: 260, flex: '0 1 320px' }} />
        {[['fase', 'Fase', 190], ['impact', 'Impact', 120], ['urgentie', 'Urgentie', 150], ['type', 'Type', 180]].map(([k, label, w]) => (
          <TextField key={k} select size="small" label={label} value={filter[k]} onChange={zetF(k)} sx={{ width: w }}>
            <MenuItem value="">Alle</MenuItem>
            {(opties[k] || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        ))}
        <TextField select size="small" label="Status" value={filter.statussen} onChange={zetF('statussen')} sx={{ width: 200 }}
          SelectProps={{ multiple: true, renderValue: (v) => (v.length ? `${v.length} van ${statussen.length}` : 'Alle') }}>
          {statussen.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        {filterActief && <Button size="small" onClick={() => setFilter(LEEG_FILTER)} sx={{ textTransform: 'none' }}>Wis filters</Button>}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
          {gefilterd.length} punten{data?.stand ? ` · stand Airtable ${new Date(data.stand).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}` : ''}
        </Typography>
        <Button size="small" onClick={() => laad(true)} disabled={laden} sx={{ textTransform: 'none' }}>Vernieuwen</Button>
      </Paper>

      {laden && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : data && data.punten.length === 0 ? (
        <Typography sx={{ color: '#64748b', py: 6, textAlign: 'center' }}>Nog geen verbeterpunten gespiegeld uit Airtable.</Typography>
      ) : (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', overflowX: 'auto', pb: 2 }}>
            {zichtbareStatussen.map((s) => (
              <Kolom key={s} status={s} punten={kolommen[s] || []} onOpen={(p) => setDialoog({ punt: p })} sleepbaar={schrijven} bezig={bezig} />
            ))}
          </Box>
        </DndContext>
      )}

      <VerbeterpuntDialog open={!!dialoog} punt={dialoog?.punt || null} opties={opties} personen={data?.personen || []}
        alleenLezen={!schrijven} onClose={sluitDialoog}
        onOpgeslagen={(punt) => { vervang(punt); sluitDialoog(); }} />
    </Box>
  );
}
